import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { CreditCard, Truck, ArrowLeft, Check, Loader2 } from 'lucide-react';
import { useCart } from '../contexts/CartContext';

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { items, getCartTotal, placeOrder } = useCart();
  const [isProcessing, setIsProcessing] = useState(false);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  };

  const handlePayment = async () => {
    setIsProcessing(true);
    // Simulate payment processing
    await new Promise(resolve => setTimeout(resolve, 2000));
    const order = await placeOrder();
    navigate(`/bevestiging/${order.id}`);
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 py-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground mb-4">Geen producten in je winkelwagen</h1>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Verder winkelen
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Link
          to="/winkelwagen"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Terug naar winkelwagen
        </Link>

        <h1 className="text-2xl font-bold text-foreground mb-8">Afrekenen</h1>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Order Summary */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-foreground">Bestelling overzicht</h2>

            <div className="bg-card border border-primary/10 rounded-xl overflow-hidden">
              {items.map(({ product, quantity }) => (
                <div key={product.id} className="flex gap-4 p-4 border-b border-primary/10 last:border-b-0">
                  <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-background">
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'https://via.placeholder.com/64x64?text=No+Image';
                      }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground">{product.brand}</p>
                    <h3 className="text-sm font-medium text-foreground truncate">{product.name}</h3>
                    <p className="text-sm text-muted-foreground">Aantal: {quantity}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-foreground">{formatPrice(product.price * quantity)}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-card border border-primary/10 rounded-xl p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotaal</span>
                <span className="text-foreground">{formatPrice(getCartTotal())}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Verzendkosten</span>
                <span className="text-green-500">Gratis</span>
              </div>
              <div className="border-t border-primary/10 pt-2 mt-2">
                <div className="flex justify-between">
                  <span className="font-semibold text-foreground">Totaal</span>
                  <span className="text-xl font-bold text-foreground">{formatPrice(getCartTotal())}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Section */}
          <div className="space-y-6">
            {/* Delivery Info */}
            <div className="bg-card border border-primary/10 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Truck className="w-5 h-5 text-primary" />
                </div>
                <h2 className="text-lg font-semibold text-foreground">Bezorging</h2>
              </div>
              <p className="text-sm text-muted-foreground mb-2">
                Bezorging op het adres in je account
              </p>
              <div className="flex items-center gap-2 text-green-500">
                <Check className="w-4 h-4" />
                <span className="text-sm font-medium">Gratis bezorging</span>
              </div>
            </div>

            {/* Payment Method */}
            <div className="bg-card border border-primary/10 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-primary/10">
                  <CreditCard className="w-5 h-5 text-primary" />
                </div>
                <h2 className="text-lg font-semibold text-foreground">Betaalmethode</h2>
              </div>

              <div className="space-y-3">
                <label className="flex items-center gap-3 p-3 border border-primary/30 bg-primary/5 rounded-lg cursor-pointer">
                  <input type="radio" name="payment" defaultChecked className="text-primary" />
                  <span className="font-medium text-foreground">iDEAL</span>
                </label>
                <label className="flex items-center gap-3 p-3 border border-primary/10 rounded-lg cursor-pointer hover:border-primary/30 transition-colors">
                  <input type="radio" name="payment" className="text-primary" />
                  <span className="font-medium text-foreground">Creditcard</span>
                </label>
                <label className="flex items-center gap-3 p-3 border border-primary/10 rounded-lg cursor-pointer hover:border-primary/30 transition-colors">
                  <input type="radio" name="payment" className="text-primary" />
                  <span className="font-medium text-foreground">PayPal</span>
                </label>
              </div>
            </div>

            {/* Pay Button */}
            <button
              onClick={handlePayment}
              disabled={isProcessing}
              className="w-full py-4 bg-primary text-white font-semibold rounded-xl hover:bg-primary/90 disabled:opacity-70 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Betaling verwerken...
                </>
              ) : (
                <>
                  Betaal {formatPrice(getCartTotal())}
                </>
              )}
            </button>

            <p className="text-xs text-center text-muted-foreground">
              Dit is een demo. Er wordt geen echte betaling uitgevoerd.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
